import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Alert,
  Group,
  Divider,
} from "@mantine/core";
import { notifySuccess } from "../../utils/notify";
import { IconAlertCircle, IconPencil } from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useResponsive } from "../../context/ResponsiveContext";
import GiftCardCodeField from "../common/GiftCardCodeField";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";

interface EditGiftCardCodeModalProps {
  opened: boolean;
  onClose: () => void;
  salonId: string;
  giftCardId: string;
  /** The card's current code — pre-fills the field and gates "actually changed". */
  currentCode: string;
  onSuccess: () => void | Promise<void>;
}

/** Same code (case-insensitive) means nothing to save. */
const isUnchanged = (next: string, current: string): boolean =>
  next.trim().toLowerCase() === current.trim().toLowerCase();

/**
 * Rename a held gift card's human-facing code (e.g. to match a physical card).
 * A lightweight change — no reason/note and no ledger entry (the backend relies
 * on the card's `updated_dt`). Reuses the shared `GiftCardCodeField` for format
 * validation + the debounced availability probe, with `currentCode` so the
 * pre-filled value isn't flagged as taken.
 */
export default function EditGiftCardCodeModal({
  opened,
  onClose,
  salonId,
  giftCardId,
  currentCode,
  onSuccess,
}: EditGiftCardCodeModalProps) {
  const { isMobile } = useResponsive();
  const { updateGiftCardCode } = useApp();

  const [code, setCode] = useState(currentCode);
  const [codeValid, setCodeValid] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setCode(currentCode);
      setCodeValid(true);
      setError(null);
      setIsSubmitting(false);
    }
  }, [opened, currentCode]);

  const changed = !isUnchanged(code, currentCode);
  const canSubmit = codeValid && changed && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await updateGiftCardCode(salonId, giftCardId, { code: code.trim() });
      notifySuccess("The gift card code was updated.", {
        title: "Code updated",
      });
      try {
        await Promise.resolve(onSuccess());
      } catch (e) {
        console.error("Error in edit-gift-card-code success handler:", e);
      }
      onClose();
    } catch (err) {
      console.error("Error updating gift card code:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to update the gift card code. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Edit code</Text>}
      size="md"
      fullScreen={isMobile}
      centered
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <BrandedLoadingOverlay visible={isSubmitting} />

      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        <Text size="sm" c="dimmed">
          Give this gift card a new code — for example, to match a physical
          card.
        </Text>

        <GiftCardCodeField
          salonId={salonId}
          value={code}
          onChange={setCode}
          onValidityChange={setCodeValid}
          disabled={isSubmitting}
          required
          currentCode={currentCode}
          label="Gift card code"
        />

        <Divider />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="var(--brand-teal)"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!canSubmit}
            leftSection={<IconPencil size={18} />}
          >
            Save code
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
